/**
 * Freshness — the "grey the source icon, keep the last good data with an
 * 'as of' mark" rule (write-door §3, freshness ticket 09). Never blank,
 * never silently wrong.
 */

import { dayPhase } from './day-arc';
import type { Envelope } from './types';

export type SourceKey = 'defs' | 'calendar' | 'reminders' | 'signal' | 'state';

/** Stale after ~2x the source's refresh cadence (write-door §3 table). */
const STALE_AFTER_MS: Record<SourceKey, number | null> = {
  defs: null, // push-synced; never auto-stale, grey only on a failed KV read
  calendar: 10 * 60_000,
  reminders: 10 * 60_000,
  signal: 2 * 60 * 60_000,
  state: null, // live query per poll; grey when the query fails
};

export interface SourceStatus {
  key: SourceKey;
  /** the envelope's own ok flag. */
  ok: boolean;
  /** ok, but older than 2x its cadence. */
  stale: boolean;
  /** ok && !stale — the only state that renders at full ink. */
  healthy: boolean;
  /** "as of HH:MM", shown whenever the source is not healthy. */
  asOf: string;
  fetchedAt: string;
}

export function localHm(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hm = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? hm : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${hm}`;
}

export function sourceStatus(
  key: SourceKey,
  envelope: Envelope<unknown> | null | undefined,
  now: Date = new Date(),
): SourceStatus {
  if (!envelope) {
    return { key, ok: false, stale: true, healthy: false, asOf: '—', fetchedAt: '' };
  }
  const limit = STALE_AFTER_MS[key];
  const age = now.getTime() - Date.parse(envelope.fetchedAt);
  const stale = limit !== null && Number.isFinite(age) && age > limit;
  const ok = envelope.ok !== false;
  return {
    key,
    ok,
    stale,
    healthy: ok && !stale,
    asOf: localHm(envelope.fetchedAt, now),
    fetchedAt: envelope.fetchedAt,
  };
}

/**
 * Poll cadence (freshness ticket 09 / write-door §3): ~60s while visible,
 * taper to ~10 min after 22:00, stop between 01:00 and the morning.
 *
 * The hours are the day arc's own (`day-arc.ts`), so the request rhythm and
 * the visual faces can never drift apart: the board polls hard while it is a
 * board, tapers while it is resting, and stops while it is dark.
 */
export function pollDelayMs(now: Date = new Date()): number | null {
  switch (dayPhase(now)) {
    case 'night':
      return null; // stopped until morning
    case 'invite':
      return 10 * 60_000;
    default:
      return 60_000;
  }
}
